import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, ShieldCheck } from 'lucide-react';
import { AuthHeading, AuthLayout } from './AuthLayout';
import { useAuth } from './AuthContext';
import { Button } from '@/components/ui/Button';
import { Checkbox, Field, Input } from '@/components/ui/Field';
import { FormAlert } from '@/components/ui/Feedback';
import { ApiError } from '@/lib/api';

export function SignInPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/people';

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await signIn(email.trim(), password, remember);
      navigate(redirectTo, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'network_error', 'Could not reach the server. Check your connection and try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // A 422 carries per-field messages; anything else is a form-level failure.
  const fieldError = (field: string) => (error?.status === 422 ? error.fieldError(field) : undefined);
  const formError = error && error.status !== 422 ? error.message : null;

  return (
    <AuthLayout>
      <AuthHeading
        eyebrow="Welcome back"
        title="Sign in to OneParacon"
        description="Use your company account to access projects, people and compliance information."
      />

      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {formError && <FormAlert>{formError}</FormAlert>}

        <Field label="Work email" htmlFor="email" error={fieldError('email')}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            placeholder="name@paracon.com.au"
            leadingIcon={<Mail />}
            state={fieldError('email') ? 'error' : 'default'}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Password" htmlFor="password" error={fieldError('password')}>
          <Input
            id="password"
            name="password"
            type={reveal ? 'text' : 'password'}
            autoComplete="current-password"
            required
            placeholder="Enter your password"
            leadingIcon={<Lock />}
            state={fieldError('password') ? 'error' : 'default'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            trailingSlot={
              <button
                type="button"
                onClick={() => setReveal((value) => !value)}
                aria-label={reveal ? 'Hide password' : 'Show password'}
                className="rounded p-1.5 text-muted transition-colors hover:bg-cloud hover:text-body"
              >
                {reveal ? <EyeOff aria-hidden className="h-4 w-4" /> : <Eye aria-hidden className="h-4 w-4" />}
              </button>
            }
          />
        </Field>

        <div className="flex items-center justify-between gap-4">
          <Checkbox
            label="Remember me"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
          />
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-digital transition-colors hover:text-digital-600"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" size="lg" fullWidth loading={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <hr className="my-6 border-line" />

      <p className="flex items-start gap-2.5 text-helper text-muted">
        <ShieldCheck aria-hidden className="mt-px h-4 w-4 shrink-0 text-signal" />
        Your account is protected by secure, role-based access.
      </p>

      <p className="mt-4 text-helper leading-relaxed text-muted">
        Need help accessing your account?{' '}
        <a
          href="mailto:support@paracon.com.au"
          className="font-semibold text-digital transition-colors hover:text-digital-600"
        >
          Contact your OneParacon administrator
        </a>
      </p>
    </AuthLayout>
  );
}
